// Fills in the font placeholders the visual email editor writes ({{header_font}},
// {{paragraph_font}}, {{font_face_css}}) for the admin preview.
// Keep in sync with buildEmailFontContext in backend/services/emailService.js.

const RAW_BASE = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

const assetUrl = (p) => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  return `${RAW_BASE}${p.startsWith('/') ? '' : '/'}${p}`;
};

const fontFormat = (u) => {
  const ext = u.split('?')[0].split('.').pop().toLowerCase();
  if (ext === 'otf') return 'opentype';
  if (ext === 'woff' || ext === 'woff2') return ext;
  return 'truetype';
};

const safeName = (n, fallback) => String(n || '').replace(/['"\;{}<>]/g, '').trim() || fallback;

// Emails saved by an earlier version of the visual editor have Arial written into them.
const upgradeLegacyBuilderFonts = (html) => {
  if (typeof html !== 'string' || !html.includes('<!--dp-email-builder:') || /\{\{(paragraph_font|header_font)\}\}/.test(html)) {
    return html;
  }
  return html
    .replace(/(<h[1-6]\b[^>]*style="[^"]*?)font-family:\s*Arial, Helvetica, sans-serif/g, '$1font-family:{{header_font}}')
    .replace(/font-family:\s*Arial, Helvetica, sans-serif/g, 'font-family:{{paragraph_font}}')
    .replace(/(<!--dp-email-builder:[^>]*-->)/, '$1\n<style>{{font_face_css}}</style>');
};

export function applyPreviewFonts(rawHtml, fontSettings) {
  const html = upgradeLegacyBuilderFonts(rawHtml);
  if (typeof html !== 'string' || !/\{\{(paragraph_font|header_font|font_face_css)\}\}/.test(html)) {
    return html;
  }
  const fs = fontSettings || {};
  const faces = [];
  let paragraph;
  let header;

  if (fs.paragraph_font_file_url) {
    const name = safeName(fs.paragraph_font_name, 'CampaignParagraphFont');
    const url = assetUrl(fs.paragraph_font_file_url);
    faces.push(`@font-face{font-family:'${name}';src:url('${url}') format('${fontFormat(url)}');}`);
    paragraph = `'${name}', Arial, Helvetica, sans-serif`;
  } else {
    // System default: the app already loads these (see App.css)
    paragraph = "'GreycliffCF Regular', Arial, Helvetica, sans-serif";
  }

  if (fs.header_font_file_url) {
    const name = safeName(fs.header_font_name, 'CampaignHeaderFont');
    const url = assetUrl(fs.header_font_file_url);
    faces.push(`@font-face{font-family:'${name}';src:url('${url}') format('${fontFormat(url)}');}`);
    header = `'${name}', Georgia, 'Times New Roman', serif`;
  } else {
    header = "'MADE Dillan', Georgia, 'Times New Roman', serif";
  }

  return html
    .replace(/\{\{paragraph_font\}\}/g, paragraph)
    .replace(/\{\{header_font\}\}/g, header)
    .replace(/\{\{font_face_css\}\}/g, faces.join(''));
}

// Extra preview-only CSS. The admin app forces h1/h2 to its own font with !important, so a
// custom header font needs a more specific !important rule to show in the preview.
// With the default paragraph font, bold uses the heavier face the app already loads.
export const previewFontCss = (fontSettings) => {
  const fs = fontSettings || {};
  let css = '';
  if (fs.header_font_file_url) {
    const name = safeName(fs.header_font_name, 'CampaignHeaderFont');
    css += `.email-preview-root :is(h1, h2, h3, h4)[style*="${name}"] { font-family: '${name}', Georgia, 'Times New Roman', serif !important; }`;
  }
  if (!fs.paragraph_font_file_url) {
    css += " .email-preview-root b, .email-preview-root strong, .email-preview-root [style*='font-weight'] { font-family: 'GreycliffCF ExtraBold', 'GreycliffCF Heavy', Arial, sans-serif !important; }";
  }
  return css;
};
